import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, Lock } from "lucide-react";
import { Link } from "wouter";

type UpgradeModalMode = "login" | "upgrade";

interface UpgradeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: UpgradeModalMode;
  featureName?: string;
  returnTo?: string;
}

const freeFeatures = ["saving videos"];

const paidFeatures = [
  "120 min uploaded video storage",
  "Notes on saved videos",
  "Playlists to organize your library",
];

export function UpgradeModal({ open, onOpenChange, mode, featureName = "this feature", returnTo }: UpgradeModalProps) {
  const loginUrl = returnTo ? `/auth/google?returnTo=${encodeURIComponent(returnTo)}` : "/auth/google";
  const isFreeFeature = freeFeatures.includes(featureName);

  if (mode === "login") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md" data-testid="modal-login-prompt">
          <DialogHeader>
            <div className="mx-auto w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center mb-2">
              <Lock className="w-6 h-6 text-primary" />
            </div>
            <DialogTitle className="text-center">
              {isFreeFeature ? "Log in to continue" : "Subscription required"}
            </DialogTitle>
            <DialogDescription className="text-center">
              {isFreeFeature
                ? `Create a free account or log in to start ${featureName}.`
                : `${featureName.charAt(0).toUpperCase() + featureName.slice(1)} is a premium feature. Sign in and subscribe to unlock it.`}
            </DialogDescription>
          </DialogHeader>
          {!isFreeFeature && (
            <ul className="space-y-2.5 my-2">
              {paidFeatures.map((feature) => (
                <li key={feature} className="flex items-center gap-2.5 text-sm">
                  <Check className="w-4 h-4 text-primary shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-col gap-3 mt-2">
            <a href={loginUrl}>
              <Button className="w-full" data-testid="button-modal-login">
                Sign in with Google
              </Button>
            </a>
            {!isFreeFeature && (
              <Link href="/pricing" onClick={() => onOpenChange(false)}>
                <Button variant="outline" className="w-full">
                  Learn about plans
                </Button>
              </Link>
            )}
            <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="button-modal-cancel">
              Maybe later
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="modal-upgrade-prompt">
        <DialogHeader>
          <div className="mx-auto w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center mb-2">
            <Lock className="w-6 h-6 text-primary" />
          </div>
          <DialogTitle className="text-center">Upgrade to unlock {featureName}</DialogTitle>
          <DialogDescription className="text-center">
            Get access to all premium features — or pay once for lifetime access.
          </DialogDescription>
        </DialogHeader>
        <ul className="space-y-2.5 my-4">
          {paidFeatures.map((feature) => (
            <li key={feature} className="flex items-center gap-2.5 text-sm">
              <Check className="w-4 h-4 text-primary shrink-0" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
        <div className="flex flex-col gap-3">
          <Link href="/pricing" onClick={() => onOpenChange(false)}>
            <Button className="w-full" data-testid="button-modal-upgrade">
              View Plans
            </Button>
          </Link>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="button-modal-cancel">
            Maybe later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
